import { shouldPlay, type ThreeCardState } from '../../engine/games/threeCardPoker';
import { scoreThree, THREE_CARD_NAMES, ThreeCardRank } from '../../engine/poker';
import type { Card } from '../../engine/cards';
import { withCommas } from '../../format';
import { cardsIn, PlayingCard } from '../parts';
import { Burst, Controls, secondChanceFrame, Stage, StatusLine, useAnimClock, useBurst, useLuckMoment, type TableProps } from './Frame';

const FLIP_MS = 500;
const LOST_MS = 900;
const BANNER_MS = 1100;
const END_HOLD_MS = 300;
const HIGH: Record<number, string> = { 14: 'Ace', 13: 'King', 12: 'Queen', 11: 'Jack' };

function handName(cards: Card[]): string {
  const s = scoreThree(cards);
  if (s[0] === ThreeCardRank.HighCard) return `${HIGH[s[1]!] ?? s[1]} high`;
  return THREE_CARD_NAMES[s[0] as ThreeCardRank];
}

function Row({ label, cards, shown, note }: { label: string; cards: Card[]; shown: number; note?: string }) {
  return (
    <div className="tc-row">
      <div className="tc-label">{label}{note && <span className="tc-note"> · {note}</span>}</div>
      <div className="cards">
        {[0, 1, 2].map((i) => (i < shown && cards[i] !== undefined ? <PlayingCard key={i} card={cards[i]} /> : <PlayingCard key={i} hidden />))}
      </div>
    </div>
  );
}

export function ThreeCardTable({ snapshot, actions }: TableProps) {
  const run = snapshot.run!;
  const state = run.tables.threecard?.state as ThreeCardState | undefined;
  const anim = snapshot.anim;
  const luck = secondChanceFrame(snapshot);
  const lostDealer = luck ? cardsIn(luck.split('\n').find((l) => l.startsWith('Dealer:')) ?? '') : [];
  const hasLost = lostDealer.length === 3;
  const firstEnd = hasLost ? 3 * FLIP_MS + LOST_MS + BANNER_MS : 0;
  const elapsed = useAnimClock(snapshot, actions, firstEnd + 3 * FLIP_MS + END_HOLD_MS);
  const [burst, fading, setBurst] = useBurst();
  useLuckMoment(snapshot, elapsed, hasLost ? 3 * FLIP_MS + LOST_MS : null, () => ({ what: "The dealer's winning hand is re-dealt.", saved: true }), setBurst);

  if (!state || state.phase === 'idle') {
    return (
      <>
        <Stage className="tc-stage">
          <Row label="Dealer" cards={[]} shown={0} />
          <p className="felt-hint">Your bet is the ante. Look at your three cards, then play (the same again) or fold. The dealer needs Queen high to qualify.</p>
          <Row label="You" cards={[]} shown={0} />
        </Stage>
        <Controls snapshot={snapshot} actions={actions} />
      </>
    );
  }

  // The dealer's cards turn over one at a time (only if you played).
  let dealer = state.dealer;
  let shown = state.phase === 'done' && state.played ? 3 : 0;
  if (anim && state.played) {
    if (hasLost && elapsed < firstEnd) {
      dealer = lostDealer;
      shown = Math.min(3, 1 + Math.floor(elapsed / FLIP_MS));
    } else {
      shown = Math.min(3, 1 + Math.floor((elapsed - firstEnd) / FLIP_MS));
    }
  }
  const deciding = state.phase === 'decide';
  const finished = state.phase === 'done' && !anim;

  return (
    <>
      <Stage className="tc-stage" onClick={anim ? actions.skip : undefined}>
        <Burst burst={burst} fading={fading} pending={false} />
        <Row label="Dealer" cards={dealer} shown={shown} note={shown === 3 ? handName(dealer) : state.phase === 'done' && !state.played ? 'not shown' : undefined} />
        <Row label="You" cards={state.player} shown={3} note={handName(state.player)} />
        {deciding && <p className="tc-hint">{shouldPlay(state.player) ? 'Q-6-4 or better: worth playing.' : 'Below Q-6-4: most players fold.'}</p>}
        <StatusLine snapshot={snapshot} result={finished ? state.lines.map((l, i) => <div key={i}>{withCommas(l)}</div>) : undefined} tone={finished ? (state.returned > state.ante * (state.played ? 2 : 1) ? 'win' : state.returned < state.ante * (state.played ? 2 : 1) ? 'loss' : '') : ''} />
      </Stage>
      <Controls snapshot={snapshot} actions={actions} primaryId="play" onAct={() => setBurst(null)} />
    </>
  );
}
